import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import WithLoader from 'ui/WithLoader/WithLoader';
import {
    BudgetProposalT,
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { Box } from '@mui/material';
import { BudgetExpenseOrIncome } from 'business/constants';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { getInscriptionInformation } from '../dataHelpers';
import MoveInscription from '../MoveInscription/MoveInscription';
import { useDeleteInscription } from '../hooks/inscriptions';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

type ActionsCellRendererParams = CellRendererParams<InscriptionWithMetadata> & {
    expenseOrIncome: BudgetExpenseOrIncome;
    budgetProposals: BudgetProposalT[];
    inscriptionIdColumn: keyof InscriptionT;
    queryCacheKey: InscriptionQueryKey;
};

export default function ActionsCellRenderer({
    data,
    api,
    expenseOrIncome,
    budgetProposals,
    inscriptionIdColumn,
    queryCacheKey,
}: ActionsCellRendererParams): React.ReactElement {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [anchorEl, setAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);
    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [selectedInscription, setSelectedInscription] =
        useState<InscriptionT | null>(null);
    const statusesSettings = useContext(StatusSettingsContext);
    const { inscription, inscriptionId, canEdit, budgetProposal } =
        getInscriptionInformation(
            data,
            budgetProposals,
            statusesSettings,
            inscriptionIdColumn,
        );

    const deleteInscriptionMutation = useDeleteInscription(
        queryCacheKey,
        dispatch,
        queryClient,
        inscriptionId,
        inscriptionIdColumn,
    );

    if (!data || budgetProposal === undefined) {
        return <span />;
    }

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const showMoveModal = (ins: InscriptionT | null) => {
        setSelectedInscription(ins);
        setShowAssignmentModal(true);
    };

    const inscriptions: InscriptionWithMetadata[] = [];
    api.forEachNode((node) => {
        // Some node.data are undefined when we are doing some row grouping
        // The undefined node.data represent aggregation nodes
        if (node.data) inscriptions.push(node.data);
    });
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
                size="small"
                onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                }}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={closeMenu}
            >
                <MenuItem
                    disabled={!data.inscription.canMove}
                    onClick={() => {
                        closeMenu();

                        showMoveModal(inscription);
                    }}
                >
                    <ListItemIcon>
                        <OpenWithIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('common.move')} />
                </MenuItem>
                <WithLoader isLoading={deleteInscriptionMutation.isLoading}>
                    <MenuItem
                        disabled={
                            !canEdit || deleteInscriptionMutation.isLoading
                        }
                        onClick={() => {
                            closeMenu();
                            setShowConfirmDeleteModal(true);
                        }}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('common.delete')} />
                    </MenuItem>
                </WithLoader>
            </Menu>
            <ConfirmDeleteModal
                show={showConfirmDeleteModal}
                cancel={() => {
                    setShowConfirmDeleteModal(false);
                }}
                confirm={() => {
                    setShowConfirmDeleteModal(false);
                    deleteInscriptionMutation.mutate();
                }}
                title={t('budgetProposal.inscriptionDeleteConfirm')}
            />
            {showAssignmentModal && selectedInscription && (
                <MoveInscription
                    isOpen={showAssignmentModal}
                    onClose={() => setShowAssignmentModal(false)}
                    budgetProposal={budgetProposal}
                    inscriptionIdColumn={inscriptionIdColumn}
                    inscriptions={inscriptions}
                    queryCacheKey={queryCacheKey}
                    selectedInscription={selectedInscription}
                    budgetProposals={budgetProposals}
                    expenseOrIncome={expenseOrIncome}
                />
            )}
        </Box>
    );
}
